export type SseFrame = {
  event: string;
  id?: string;
  data: string;
};

const MAX_FRAME_LENGTH = 1_000_000;

export async function* decodeSse(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<SseFrame> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let event = "";
  let id: string | undefined;
  let dataLines: string[] = [];
  let frameLength = 0;

  const dispatch = () => {
    if (dataLines.length === 0) {
      event = "";
      id = undefined;
      frameLength = 0;
      return undefined;
    }

    const frame: SseFrame = {
      event: event || "message",
      id,
      data: dataLines.join("\n"),
    };

    event = "";
    id = undefined;
    dataLines = [];
    frameLength = 0;
    return frame;
  };

  const processLine = (line: string) => {
    if (line === "") {
      return dispatch();
    }

    if (line.startsWith(":")) {
      return undefined;
    }

    frameLength += line.length;
    if (frameLength > MAX_FRAME_LENGTH) {
      throw new Error("SSE frame exceeded the maximum supported size.");
    }

    const separator = line.indexOf(":");
    const field = separator === -1 ? line : line.slice(0, separator);
    let value = separator === -1 ? "" : line.slice(separator + 1);

    if (value.startsWith(" ")) {
      value = value.slice(1);
    }

    switch (field) {
      case "event":
        event = value;
        break;
      case "id":
        if (!value.includes("\0")) {
          id = value;
        }
        break;
      case "data":
        dataLines.push(value);
        break;
    }

    return undefined;
  };

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        buffer += decoder.decode();
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      if (buffer.length + frameLength > MAX_FRAME_LENGTH) {
        throw new Error("SSE frame exceeded the maximum supported size.");
      }

      let newlineIndex = buffer.indexOf("\n");
      while (newlineIndex !== -1) {
        let line = buffer.slice(0, newlineIndex);
        buffer = buffer.slice(newlineIndex + 1);

        if (line.endsWith("\r")) {
          line = line.slice(0, -1);
        }

        const frame = processLine(line);
        if (frame) {
          yield frame;
        }

        newlineIndex = buffer.indexOf("\n");
      }
    }

    if (buffer.length > 0) {
      const line = buffer.endsWith("\r") ? buffer.slice(0, -1) : buffer;
      const frame = processLine(line);
      if (frame) {
        yield frame;
      }
    }

    const finalFrame = dispatch();
    if (finalFrame) {
      yield finalFrame;
    }
  } finally {
    try {
      await reader.cancel();
    } catch {
      // An aborted fetch can error the body before cleanup reaches the reader.
    }
    reader.releaseLock();
  }
}
